import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import Loader from "../../components/Loader.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "../../utils/Notifications.tsx";
import {
  getComplaint,
  updateComplaint,
} from "../../services/complaint.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import moment from "moment";
import "moment/locale/es";
import S3Service from "../../services/s3.service.ts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
moment.locale("es");

const InfoRow = ({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: React.ReactNode;
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: "0.75rem",
      padding: "0.75rem 0",
      borderBottom: "1px solid #f3f4f6",
    }}
  >
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginTop: 2,
      }}
    >
      <i className={`fa-solid ${icon}`} style={{ fontSize: 12, color: "#6b7280" }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: "#111827", fontWeight: 500, wordBreak: "break-word" }}>
        {value}
      </div>
    </div>
  </div>
);

const EditComplaint = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [complaint, setComplaint] = useState<any>(null);
  const [profileUrl, setProfileUrl] = useState("");
  const { user } = useSelector((state: RootState) => state.auth);
  useAppSettings();

  const loadComplaint = async () => {
    try {
      setInitialLoading(true);
      const complaintData = await getComplaint(Number(id));
      setComplaint(complaintData);

      if (complaintData.partner?.person?.curp) {
        try {
          const file = await S3Service.getFile(
            "PERFIL_" + complaintData.partner.person.curp
          );
          setProfileUrl(file?.url || "");
        } catch {
          setProfileUrl("");
        }
      } else {
        setProfileUrl("");
      }
    } catch (error) {
      console.error("Error fetching complaint:", error);
      notification("Error", "No se pudo cargar la queja", "danger");
      navigate("/quejas");
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadComplaint();
    }
  }, [id]);

  const handleMarkAsReviewed = async () => {
    if (!user?.id) {
      notification("Error", "No se pudo obtener la información del usuario", "danger");
      return;
    }
    setLoading(true);
    try {
      await updateComplaint(Number(id), {
        reviewerId: user.id,
        reviewedAt: new Date().toISOString(),
        status: "revisado",
      });
      notification("Éxito", "Queja marcada como revisada", "success");
      loadComplaint();
    } catch (error: any) {
      notification("Error", error?.message || "No se pudo actualizar la queja", "danger");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    if (!date) return "-";
    return moment(date).format("D [de] MMMM [de] YYYY");
  };

  const isReviewed = complaint?.status === "revisado";

  if (initialLoading) {
    return (
      <Layout>
        <Loader isLoading={true} />
      </Layout>
    );
  }

  if (!complaint) return null;

  const partnerName = complaint.partner?.person
    ? toCamelCase(
        `${complaint.partner.person.firstName || ""} ${complaint.partner.person.lastName || ""} ${complaint.partner.person.secondLastName || ""}`.trim()
      )
    : null;

  return (
    <Layout>
      <Title baseTitle="Quejas" basePath="/quejas" title="Detalle de queja" />

      <div style={{ padding: "1rem"}}>

        {/* Hero header */}
        <div
          style={{
            background: isReviewed
              ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
              : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
            border: `1px solid ${isReviewed ? "#bbf7d0" : "#fde68a"}`,
            borderRadius: 16,
            padding: "1.5rem",
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", minWidth: 0 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: isReviewed ? "#15803d" : "#d97706",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i
                className={`fa-solid ${isReviewed ? "fa-check" : "fa-clock"}`}
                style={{ fontSize: 20, color: "#fff" }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: isReviewed ? "#15803d" : "#92400e", marginBottom: 2 }}>
                {isReviewed ? "Queja revisada" : "Queja pendiente"}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", wordBreak: "break-word" }}>
                {complaint.name}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                Registrada el {formatDate(complaint.createdAt)}
                {complaint.anonymous && (
                  <span style={{
                    marginLeft: "0.5rem",
                    background: "#f3f4f6", color: "#6b7280",
                    padding: "0.1rem 0.5rem", borderRadius: 999,
                    fontSize: 11, fontWeight: 600,
                  }}>
                    <i className="fa-solid fa-user-secret" style={{ marginRight: 4, fontSize: 10 }} />
                    Anónima
                  </span>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Contenido principal */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) minmax(0, 2fr)",
            gap: "1.25rem",
          }}
          className="complaint-detail-grid"
        >
          {/* Columna izquierda: quién lo creó */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            {/* Header de la card */}
            <div style={{
              padding: "1rem 1.25rem",
              borderBottom: "1px solid #f3f4f6",
              display: "flex", alignItems: "center", gap: "0.5rem",
            }}>
              <i className="fa-solid fa-user" style={{ fontSize: 13, color: "#6b7280" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Colaborador</span>
            </div>

            <div style={{ padding: "1.5rem", textAlign: "center" }}>
              {complaint.anonymous ? (
                <>
                  <div style={{
                    width: 80, height: 80, borderRadius: "50%",
                    background: "#f3f4f6", border: "3px solid #e5e7eb",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 1rem",
                  }}>
                    <i className="fa-solid fa-user-secret" style={{ fontSize: 32, color: "#9ca3af" }} />
                  </div>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: "0.35rem",
                    background: "#f3f4f6", color: "#6b7280",
                    padding: "0.3rem 0.75rem", borderRadius: 999,
                    fontSize: 12, fontWeight: 600, marginBottom: "0.5rem",
                  }}>
                    <i className="fa-solid fa-eye-slash" style={{ fontSize: 10 }} />
                    Identidad oculta
                  </div>
                  <p style={{ fontSize: 12, color: "#9ca3af", margin: "0.5rem 0 0" }}>
                    Esta queja fue enviada de forma anónima
                  </p>
                </>
              ) : complaint.partner ? (
                <>
                  <img
                    src={profileUrl || "/assets/img/avatar.jpg"}
                    alt="Foto"
                    onError={(e: any) => { e.target.src = "/assets/img/avatar.jpg"; }}
                    style={{
                      width: 80, height: 80, borderRadius: "50%",
                      objectFit: "cover",
                      border: "3px solid #e5e7eb",
                      marginBottom: "1rem",
                    }}
                  />
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: "0.25rem" }}>
                    {partnerName || "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: "1rem" }}>
                    {complaint.partner?.candidate?.vacancy?.position?.name || ""}
                  </div>
                  <div style={{
                    background: "#f8fafc", borderRadius: 10, padding: "0.65rem",
                    border: "1px solid #e5e7eb", textAlign: "left",
                  }}>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>Sucursal</div>
                    <div style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>
                      {complaint.partner?.candidate?.branch?.name || "-"}
                    </div>
                  </div>
                </>
              ) : (
                <div style={{ color: "#9ca3af", fontSize: 13 }}>Sin información</div>
              )}
            </div>

            {/* Revisado por (si aplica) */}
            {isReviewed && complaint.reviewer && (
              <div style={{
                margin: "0 1.25rem 1.25rem",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 10,
                padding: "0.75rem",
              }}>
                <div style={{ fontSize: 11, color: "#15803d", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
                  <i className="fa-solid fa-check-circle" style={{ marginRight: 5 }} />
                  Revisado por
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                  {`${complaint.reviewer.name || ""} ${complaint.reviewer.lastName || ""}`.trim() || "-"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {formatDate(complaint.reviewedAt)}
                </div>
              </div>
            )}
          </div>

          {/* Columna derecha: detalle de la queja */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <div style={{
              padding: "1rem 1.25rem",
              borderBottom: "1px solid #f3f4f6",
              display: "flex", alignItems: "center", gap: "0.5rem",
            }}>
              <i className="fa-solid fa-file-lines" style={{ fontSize: 13, color: "#6b7280" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Información de la queja</span>
            </div>

            <div style={{ padding: "1.25rem" }}>
              <InfoRow icon="fa-tag" label="Nombre de la queja" value={complaint.name} />
              <InfoRow icon="fa-store" label="Sucursal" value={complaint.branch?.name || "-"} />
              {!complaint.anonymous && (
                <InfoRow icon="fa-briefcase" label="Puesto del colaborador" value={complaint.position?.name || "-"} />
              )}
              <InfoRow icon="fa-calendar" label="Fecha de registro" value={formatDate(complaint.createdAt)} />

              {/* Comentarios destacados */}
              <div style={{ marginTop: "1rem" }}>
                <div style={{
                  fontSize: 11, color: "#9ca3af", fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem",
                  display: "flex", alignItems: "center", gap: "0.4rem",
                }}>
                  <i className="fa-solid fa-message" style={{ fontSize: 10 }} />
                  Descripción / Comentarios
                </div>
                <div style={{
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "1rem",
                  fontSize: 14,
                  color: "#374151",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  minHeight: 100,
                }}>
                  {complaint.comments || <span style={{ color: "#9ca3af" }}>Sin comentarios</span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Barra de acciones */}
        <div style={{
          marginTop: "1.25rem",
          padding: "1rem 1.25rem",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}>
          <button
            type="button"
            onClick={() => navigate("/quejas")}
            style={{
              padding: "0.5rem 1rem", borderRadius: 8, fontSize: 13, fontWeight: 500,
              background: "#f8fafc", color: "#374151", border: "1px solid #d1d5db",
              cursor: "pointer",
            }}
          >
            <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />
            Volver
          </button>
          {!isReviewed && (
            <button
              type="button"
              onClick={handleMarkAsReviewed}
              disabled={loading}
              style={{
                padding: "0.5rem 1.1rem", borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: "#15803d", color: "#fff", border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
              }}
            >
              <i className="fa-solid fa-check" style={{ fontSize: 12 }} />
              {loading ? "Guardando..." : "Marcar como revisado"}
            </button>
          )}
        </div>
      </div>

      {/* Responsive grid */}
      <style>{`
        @media (max-width: 768px) {
          .complaint-detail-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default EditComplaint;
